import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { MaskLines, Reveal } from "../lib/motion";
import { BrandInline } from "./Brand";
import Footer from "./Footer";
import { FOUNDER } from "../data";

export default function FounderVision() {
  const heroRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="bg-[#F8F7F3]">
      {/* Header */}
      <header className="absolute top-0 inset-x-0 z-50">
        <div className="max-w-[1400px] mx-auto h-[88px] px-6 md:px-10 flex items-center justify-between">
          <Link to="/" data-testid="founder-logo-home">
            <BrandInline color="#F8F7F3" emblem="w-10 h-10" glow />
          </Link>
          <Link
            to="/"
            data-testid="founder-back-home"
            className="group inline-flex items-center gap-2 text-[#F8F7F3] font-body text-sm link-underline"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative bg-[#111] grain min-h-[100svh] flex items-center overflow-hidden">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center w-full pt-28 pb-16 lg:py-0">
          {/* Portrait */}
          <motion.div
            className="relative order-2 lg:order-1"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative w-full max-w-[440px] mx-auto lg:mx-0">
              <div className="absolute -inset-3 border border-[#B08D57]/70" />
              <div className="absolute -inset-3 border border-[#B08D57]/70 translate-x-2 translate-y-2 opacity-40" />
              <img
                src={FOUNDER.portrait}
                alt={FOUNDER.name}
                data-testid="founder-portrait"
                className="relative w-full object-cover grayscale ring-1 ring-white/10"
              />
            </div>
          </motion.div>

          {/* Intro text */}
          <div className="order-1 lg:order-2">
            <motion.p
              className="overline text-[#B08D57] mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Founder's Vision
            </motion.p>
            <h1 className="hero-title font-display text-[#F8F7F3] mb-8" data-testid="founder-heading">
              <MaskLines
                lines={["A company's greatest", "product is its name."]}
                active
                delay={0.4}
              />
            </h1>
            <motion.p
              className="font-body text-[#EDE8DF]/80 text-lg md:text-xl max-w-[560px] leading-relaxed"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              {FOUNDER.lead}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Vision body */}
      <section className="bg-[#F8F7F3] py-24 md:py-36">
        <div className="max-w-[760px] mx-auto px-6">
          {FOUNDER.paragraphs.slice(0, 4).map((p, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <p
                className={`font-body text-[#333] text-lg md:text-[19px] leading-[1.85] mb-8 ${
                  i === 0
                    ? "first-letter:font-display first-letter:text-[64px] first-letter:leading-[0.8] first-letter:float-left first-letter:mr-3 first-letter:mt-2 first-letter:text-[#B08D57]"
                    : ""
                }`}
              >
                {p}
              </p>
            </Reveal>
          ))}

          {/* Pull quote */}
          <Reveal>
            <blockquote className="my-16 border-l-2 border-[#B08D57] pl-8">
              <p className="font-display italic text-2xl md:text-3xl text-[#111] leading-snug">
                {FOUNDER.pullquote}
              </p>
            </blockquote>
          </Reveal>

          {/* House statement */}
          <Reveal>
            <div className="my-16 bg-[#111] text-center px-8 py-14 md:px-14" data-testid="founder-house-statement">
              <p className="overline text-[#B08D57] mb-6">The House of AGRAGA Velora</p>
              <p className="font-display text-[#F8F7F3] text-2xl md:text-4xl leading-snug">
                {FOUNDER.houseStatement}
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-10">
                {FOUNDER.pillars.map((p) => (
                  <span key={p} className="rounded-full border border-white/15 px-5 py-2 font-body text-sm text-white/75">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          {FOUNDER.paragraphs.slice(4).map((p, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <p className="font-body text-[#333] text-lg md:text-[19px] leading-[1.85] mb-8">{p}</p>
            </Reveal>
          ))}

          {/* Signature */}
          <Reveal>
            <div className="mt-16 pt-10 border-t border-[#E7E1D6]" data-testid="founder-signature">
              <p className="font-signature text-[#111] text-5xl md:text-6xl leading-none mb-4">
                {FOUNDER.name}
              </p>
              <p className="font-head font-bold text-[#111] text-lg">{FOUNDER.name}</p>
              <p className="font-body text-[#666] text-sm mt-1">{FOUNDER.title}</p>
              <p className="overline text-[#B08D57] mt-3 text-[11px]">{FOUNDER.company}</p>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-14 flex flex-col sm:flex-row gap-5">
              <Link
                to="/"
                className="rounded-full bg-[#111] text-white font-head font-semibold px-8 py-4 text-center transition-colors duration-300 hover:bg-[#B08D57]"
                data-testid="founder-explore-btn"
              >
                Explore the House
              </Link>
              <a
                href="mailto:hello@agragavelora.com"
                className="rounded-full border border-[#111] text-[#111] font-head font-semibold px-8 py-4 text-center transition-colors duration-300 hover:bg-[#111] hover:text-white"
              >
                Contact the Founder's Office
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
